import { useApolloClient, useMutation } from "@apollo/client";
import {
  Breadcrumb,
  Button,
  Column,
  Input,
  Notification,
  Row,
  Select,
} from "@cea/ui-kit";
import { debounce } from "lodash-es";
import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import ClientInviteModal from "../../../components/ClientInviteModal";
import ReportsListView from "../../../components/ReportsListView/ReportsListView";
import AuthContext from "../../../contexts/AuthContext";
import OrganizationContext from "../../../contexts/OrganizationContext";
import {
  FETCH_SCM_FILES,
  FETCH_SCM_FOLDERS,
  RENAME_SCM_FILE,
  RENAME_SCM_FOLDER,
} from "../../../queries/scmServices";
import { getNotification, getOrderByObject, sortOptions } from "../../../utils";
import SCMCreateFolderModal from "../SCMCreateFolderModal";
import SCMReportUploadModal from "../SCMReportUploadModal";
import useFileFolderDeleteDialog from "../useFileFolderDeleteDialog";
import FileUploadContext from "../../../contexts/FileUploadContext";

import styles from "./scm-service-detail.module.css";

const SCMServiceDetail = () => {
  const [loading, setLoading] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [folderData, setFolderData] = useState({ files: [], folder: [] });
  const [isClientInviteModalOpen, setIsClientInviteModalOpen] = useState(false);

  const [isCreateFileModalVisible, setIsCreateFileModalVisible] =
    useState(false);

  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] =
    useState(false);

  const {
    service,
    organization,
    fetchOrganizationDetails,
    fetchServiceDetails,
  } = useContext(OrganizationContext);
  const apolloClient = useApolloClient();
  const [queryParams, setQueryParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  const { organizationId, serviceId } = useParams();
  const { setCurrentFileUploadJobId } = useContext(FileUploadContext);

  const [renameFolder, { loading: folderRenameLoading }] =
    useMutation(RENAME_SCM_FOLDER);
  const [renameFile, { loading: fileRenameLoading }] =
    useMutation(RENAME_SCM_FILE);

  const orderBy = queryParams.get("sort") || "createDate:desc";
  const folderId = queryParams.get("folderId");
  const isRenameFolderOrFileLoading =
    folderRenameLoading || fileRenameLoading ? "loading_rename" : null;

  const fetchFolders = async () => {
    try {
      setLoading("loading_data");

      const sortObject = getOrderByObject(orderBy);

      const { data } = await apolloClient.query({
        query: FETCH_SCM_FOLDERS,
        variables: {
          where: {
            id: folderId,
            organizationId: organization.id,
          },
          orderBy: sortObject,
        },
        fetchPolicy: "network-only",
      });

      if (data.getSCMFolders) {
        setFolderData(data.getSCMFolders);
      }
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    } finally {
      setLoading(null);
    }
  };

  const fetchFiles = async (variables = {}) => {
    try {
      setLoading("loading_data");

      const sortObject = getOrderByObject(orderBy);

      const { data } = await apolloClient.query({
        query: FETCH_SCM_FILES,
        variables: {
          ...variables,
          organizationId: organization.id,
          orderBy: sortObject,
        },
      });

      setFolderData({
        folders: [],
        files: data.getSCMFiles,
      });
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    if (!organization) {
      fetchOrganizationDetails(organizationId);
    }

    if (!service) {
      const filterQuery = {};

      if (serviceId) {
        filterQuery.id = serviceId;
      } else {
        filterQuery.serviceType = "scm";
      }

      fetchServiceDetails(filterQuery);
    }

    if (organization && service) {
      if (inputValue.length) {
        fetchFiles({ name: inputValue });
      } else {
        fetchFolders();
      }
    }
  }, [organization, service, folderId, orderBy]);

  const debouncer = useCallback(
    debounce((searchValue) => fetchFiles(searchValue), 750),
    []
  );

  const onNameSearch = (event) => {
    const searchValue = event.target.value;

    setInputValue(searchValue);

    if (searchValue.length) {
      debouncer({ name: searchValue });
    } else {
      fetchFolders();
    }
  };

  const showCreateFolderModal = () => {
    setIsCreateFolderModalVisible(true);
  };

  const onFolderCreate = (newFolder, parentFolder) => {
    if (!folderData?.id) {
      setFolderData({
        ...parentFolder,
        folders: [
          { ...newFolder, __typename: "SCMFolder", createdBy: user.name },
        ],
        files: [],
        parentId: null,
        id: parentFolder.parentId,
        name: parentFolder.parentName,
      });
    } else {
      setFolderData((prevState) => ({
        ...prevState,
        folders: [
          ...prevState.folders,
          {
            ...newFolder,
            __typename: "SCMFolder",
            createdBy: user.name,
          },
        ],
      }));
    }

    setIsCreateFolderModalVisible(false);
  };

  const onFileCreate = (newFile) => {
    setFolderData((prevState) => ({
      ...prevState,
      files: [
        ...prevState.files,
        { ...newFile, __typename: "SCMFile", createdBy: user.name },
      ],
    }));

    setIsCreateFileModalVisible(false);
  };

  const onFolderOrFileRename = async (details, newName) => {
    try {
      if (details.__typename.includes("Folder")) {
        await renameFolder({
          variables: {
            data: {
              id: details.id,
              name: newName,
            },
          },
        });

        setFolderData((prevState) => ({
          ...prevState,
          folders: prevState.folders.map((item) => {
            if (item.id === details.id) {
              return {
                ...item,
                name: newName,
              };
            }

            return item;
          }),
        }));
      } else {
        await renameFile({
          variables: {
            data: {
              id: details.id,
              name: newName,
            },
          },
        });

        setFolderData((prevState) => ({
          ...prevState,
          files: prevState.files.map((item) => {
            if (item.id === details.id) {
              return {
                ...item,
                name: newName,
              };
            }

            return item;
          }),
        }));
      }
    } catch (error) {
      const message = getNotification(error);

      Notification.error({
        message,
      });
    }
  };

  const onFileUpdate = (newFile, parentFolder) => {
    if (!folderData?.id) {
      setFolderData((prevState) => ({
        ...parentFolder,
        files: prevState.files.map((file) => {
          if (file.tempId === newFile.tempId) {
            return {
              ...file,
              uploading: false,
              ...newFile,
            };
          }

          return file;
        }),
        folders: [],
        parentId: null,
        id: parentFolder.parentId,
        name: parentFolder.parentName,
      }));
    } else {
      setFolderData((prevState) => ({
        ...prevState,
        files: prevState.files.map((file) => {
          if (file.tempId === newFile.tempId) {
            return {
              ...file,
              uploading: false,
              ...newFile,
            };
          }

          return file;
        }),
      }));
    }
  };

  const onFolderDelete = (id) => {
    setFolderData((prevState) => ({
      ...prevState,
      folders: prevState.folders.filter((folder) => folder.id !== id),
    }));
  };

  const onFileDelete = (id) => {
    setFolderData((prevState) => ({
      ...prevState,
      files: prevState.files.filter((file) => file.id !== id),
    }));
  };

  const handleChange = (value = "") => {
    setSelectedOrder(value || null);

    setQueryParams({ sort: value });
  };

  const header = (
    <div className={styles["reports-filter-container"]}>
      <Row gutter={[16, 32]} justify="space-between">
        <Column span={6}>
          <Input
            placeholder="Search File"
            value={inputValue}
            onChange={onNameSearch}
          />
        </Column>
        <Column span={6}>
          <Select
            allowClear
            onChange={handleChange}
            placeholder="Sort by"
            value={selectedOrder}
            options={sortOptions}
            className={styles["search-input"]}
          />
        </Column>
      </Row>
    </div>
  );

  const navHolder = (
    <div className={styles["flex-container"]}>
      <div className="breadcrumb-holder">
        <Breadcrumb>
          <Breadcrumb.Item>Services</Breadcrumb.Item>
          <Breadcrumb.Item>{service?.name}</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className={styles["actions-container"]}>
        <Button
          className="gap-2"
          type="primary"
          size="small"
          onClick={() => {
            setIsClientInviteModalOpen(true);
          }}
        >
          Add Client User
        </Button>
        <Button
          size="small"
          type="primary"
          className="theme-button"
          onClick={() => {
            setIsCreateFileModalVisible(true);
            const fileUploadJobId = crypto.randomUUID();

            setCurrentFileUploadJobId(fileUploadJobId);
          }}
        >
          Upload File
        </Button>
        <Button
          type="primary"
          size="small"
          onClick={showCreateFolderModal}
          className="theme-button"
        >
          Create Folder
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <ReportsListView
        folderData={folderData}
        loading={loading || isRenameFolderOrFileLoading}
        onFileDelete={onFileDelete}
        onFolderDelete={onFolderDelete}
        navHolder={navHolder}
        header={header}
        onRename={onFolderOrFileRename}
        useFileFolderDeleteDialog={useFileFolderDeleteDialog}
      />
      <SCMReportUploadModal
        isVisible={isCreateFileModalVisible}
        onFileUpdate={onFileUpdate}
        onFileCreate={onFileCreate}
        handleCancel={() => {
          setIsCreateFileModalVisible(false);
        }}
        onFolderCreate={onFolderCreate}
        parentId={folderId || folderData?.id}
        shouldAllowZip
      />
      <SCMCreateFolderModal
        isVisible={isCreateFolderModalVisible}
        handleCancel={() => {
          setIsCreateFolderModalVisible(false);
        }}
        parentId={folderId || folderData?.id}
        handleOk={onFolderCreate}
      />
      <ClientInviteModal
        isVisible={isClientInviteModalOpen}
        handleCancel={() => setIsClientInviteModalOpen(false)}
      />
    </>
  );
};

export default SCMServiceDetail;
